import React from 'react';
import { useNavigate } from 'react-router-dom';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import './InvoiceSendSummaryModal.css';

const InvoiceSendSummaryModal = ({ 
  isOpen, 
  onClose, 
  summaryData, 
  generatePaymentLink
}) => {
  const navigate = useNavigate();

  const handleViewJobDetails = () => {
    if (summaryData?.job_id) {
      navigate(`/reports/jobs/${summaryData.job_id}`);
      onClose();
    }
  };

  const handleViewAllJobs = () => {
    navigate('/reports/jobs');
    onClose();
  };

  const renderSummary = () => {
    if (!summaryData) return null;

    // Check if starting (before job_id is received)
    if (summaryData.status === 'starting') {
      return (
        <div className="summary-modal-content">
          <RTSpinner message={summaryData.message || "Starting background processing..."} />
        </div>
      );
    }

    // Check if timeout occurred
    if (summaryData.status === 'timeout') {
      return (
        <div className="summary-modal-content">
          <div className="summary-info">
            <p><strong>Total Invoices:</strong> {summaryData.total_invoices}</p>
            <p className="processing-note">{summaryData.message}</p>
            <div className="job-link-note">
              The background job may still be running. Check the <strong>Background Jobs</strong> report to verify the status and view results.
            </div>
          </div>
          
          <div className="modal-actions">
            <RTButton variant='secondary' onClick={onClose}>
              Close
            </RTButton>
            <RTButton 
              variant="primary" 
              onClick={handleViewAllJobs}
            >
              View Background Jobs
            </RTButton>
          </div>
        </div>
      );
    }

    // Check if still processing in background
    if (summaryData.status === 'processing') {
      return (
        <div className="summary-modal-content">
          <div className="summary-info">
            <p><strong>Job ID:</strong> {summaryData.job_id}</p>
            <p><strong>Total Invoices:</strong> {summaryData.total_invoices}</p>
            <p className="processing-note">{summaryData.message}</p>
            <div className="job-link-note">
              You can view the job status and details in the <strong>Background Jobs</strong> report.
            </div>
          </div>
          
          <div className="modal-actions">
            <RTButton variant='secondary' onClick={onClose}>
              Close
            </RTButton>
            <RTButton 
              variant="primary" 
              onClick={handleViewJobDetails}
              className="me-2"
            >
              View Job Details
            </RTButton>
          </div>
        </div>
      );
    }

    const hasFailures = summaryData.failed_count > 0 || 
                        summaryData.payment_link_failed_count > 0 || 
                        summaryData.whatsapp_failed_count > 0;

    return (
      <div className="summary-modal-content">
        {/* Invoice Summary */}
        <div className="summary-row">
          <span className="summary-label">Total Invoices:</span>
          <span className="summary-value">{summaryData.total_invoices}</span>
        </div>
        
        <div className="summary-row">
          <span className="summary-label">Successfully Sent:</span>
          <span className="summary-value">{summaryData.sent_count}</span>
        </div>
        
        {summaryData.failed_count > 0 && (
          <div className="summary-row">
            <span className="summary-label">Failed to Send:</span>
            <span className="summary-value error">{summaryData.failed_count}</span>
          </div>
        )}

        {/* Payment Links Summary */}
        {generatePaymentLink && (
          <>
            <div className="summary-divider"></div>
            <div className="summary-section-title">Payment Links</div>
            
            <div className="summary-row">
              <span className="summary-label">Links Generated:</span>
              <span className="summary-value">{summaryData.payment_link_success_count}</span>
            </div>
            
            {summaryData.payment_link_failed_count > 0 && (
              <div className="summary-row">
                <span className="summary-label">Failed to Generate:</span>
                <span className="summary-value error">{summaryData.payment_link_failed_count}</span>
              </div>
            )}
          </>
        )}

        {/* WhatsApp Summary */}
        {generatePaymentLink && (summaryData.whatsapp_sent_count > 0 || summaryData.whatsapp_failed_count > 0) && (
          <>
            <div className="summary-divider"></div>
            <div className="summary-section-title">WhatsApp Notifications</div>
            
            {summaryData.whatsapp_sent_count > 0 && (
              <div className="summary-row">
                <span className="summary-label">Sent:</span>
                <span className="summary-value">{summaryData.whatsapp_sent_count}</span>
              </div>
            )}
            
            {summaryData.whatsapp_failed_count > 0 && (
              <div className="summary-row">
                <span className="summary-label">Failed to Send:</span>
                <span className="summary-value error">{summaryData.whatsapp_failed_count}</span>
              </div>
            )}
          </>
        )}

        {/* Warning Note */}
        {hasFailures && (
          <div className="summary-warning">
            ⚠️ Some operations failed. Check the browser console for detailed error information.
          </div>
        )}

        {/* Action Button */}
        <div className="modal-actions">
          <RTButton variant="primary" onClick={onClose}>
            Close
          </RTButton>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <RTModal
      isOpen={isOpen}
      onClose={onClose}
      title="Invoice Send Summary"
      size="medium"
    >
      {renderSummary()}
    </RTModal>
  );
};

export default InvoiceSendSummaryModal;
